export interface VttCue {
  start: number
  end: number
  text: string
}

/**
 * Parses a VTT timestamp (`HH:MM:SS.mmm` or `MM:SS.mmm`) to seconds.
 */
export function parseVttTimestamp (value: string): number {
  const token = value.trim().split(/\s/)[0]
  const parts = token.split(':')
  if (parts.length === 3) {
    return (Number(parts[0]) * 3600) + (Number(parts[1]) * 60) + Number(parts[2])
  }
  if (parts.length === 2) {
    return (Number(parts[0]) * 60) + Number(parts[1])
  }
  return Number(token)
}

/**
 * Parses a WebVTT file into cues. Cue text keeps internal line breaks.
 */
export function parseWebVtt (raw: string): VttCue[] {
  const cues: VttCue[] = []
  const normalized = raw.replace(/^\uFEFF/, '').replace(/\r\n/g, '\n')
  const blocks = normalized.split(/\n\s*\n/)

  blocks.forEach((block) => {
    const lines = block.split('\n').filter((line) => {
      const trimmed = line.trim()
      return trimmed !== '' && !trimmed.startsWith('WEBVTT') && !trimmed.startsWith('NOTE')
    })
    const timingIndex = lines.findIndex((line) => line.includes('-->'))
    if (timingIndex < 0) return

    const [startRaw, endAndSettings] = lines[timingIndex].split('-->')
    if (endAndSettings == null) return
    const text = lines.slice(timingIndex + 1).join('\n').trim()
    if (text === '') return

    cues.push({
      start: parseVttTimestamp(startRaw),
      end: parseVttTimestamp(endAndSettings),
      text
    })
  })

  return cues
}

/** Active cue text at `time` seconds, or empty when nothing is showing. */
export function cueTextAtTime (cues: VttCue[], time: number): string {
  const cue = cues.find((item) => time >= item.start && time < item.end)
  return cue?.text ?? ''
}
